function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, button, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findByLabel(text) {
	const labels = document.querySelectorAll('label, .MuiFormLabel-root, .MuiInputLabel-root')
	for (const label of labels) {
		if (label.textContent.toLowerCase().includes(text.toLowerCase())) {
			return label.closest('.MuiFormControl-root') || label.closest('.MuiGrid-item') || label.parentElement
		}
	}
	return null
}

const ES = navigator.language?.startsWith('es')

export const TOUR_STEPS = [
	{
		type: 'intro',
		icon: 'table-cells-column-lock',
		title: ES ? 'Bienvenido al Entorno de Precios' : 'Welcome to Pricing Environment',
		description: ES
			? 'El Entorno de Precios ofrece una vista multidimensional de los precios de alquiler de coches por oficinas, proveedores, fechas de recogida y semanas de reserva. Compara precios de un vistazo y sigue la evolución a lo largo del tiempo con indicadores codificados por color.'
			: 'The Pricing Environment provides a multi-dimensional view of car rental prices across offices, providers, pickup dates, and booking weeks. Compare prices at a glance and track evolution over time with color-coded indicators.',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Site') || findByText('Market')
			if (el) {
				const toggle = el.closest('.MuiToggleButtonGroup-root') || el.closest('.MuiGrid-item')
				if (toggle) return toggle
			}
			return null
		},
		icon: 'toggle-on',
		title: ES ? 'Selector Sitio / Mercado' : 'Site / Market Toggle',
		description: ES
			? 'Alterna entre filtrar por sitios web de alquiler específicos (Sitio) o por región de mercado (Mercado). Esto cambia las opciones disponibles en el desplegable de sitio/mercado de abajo.'
			: 'Switch between filtering by specific rental websites (Site) or by market region (Market). This changes the available options in the site/market dropdown below.',
		example: ES
			? 'Modo Sitio: kayak.com, rentalcars.com | Modo Mercado: ES, UK, FR'
			: 'Site mode: kayak.com, rentalcars.com | Market mode: ES, UK, FR',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByLabel('Booking weeks') || findByLabel('Weeks')
			if (el) return el.closest('.MuiGrid-item') || el
			return null
		},
		icon: 'calendar-week',
		title: ES ? 'Semanas de Reserva' : 'Booking Weeks',
		description: ES
			? 'Selecciona las ventanas de reserva a comparar. Cada semana de reserva se convierte en una subcolumna bajo cada fecha de recogida, permitiéndote ver cómo cambian los precios a medida que se acerca la fecha de alquiler.'
			: 'Select the booking windows to compare. Each booking week becomes a sub-column under each pickup date, so you can see how prices change as the rental date approaches.',
		example: ES
			? 'Semana -1, Semana -2, Semana -4 → muestra precios en diferentes periodos de antelación'
			: 'Week -1, Week -2, Week -4 → shows prices at different advance booking periods',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByLabel('Dates') || findByLabel('Pickup')
			if (el) return el.closest('.MuiGrid-item') || el
			return null
		},
		icon: 'calendar-days',
		title: ES ? 'Fechas de Recogida' : 'Pickup Dates',
		description: ES
			? 'Selecciona varias fechas de recogida para comparar. Cada fecha se convierte en un encabezado de columna principal en la tabla de resultados. Puedes añadir varias fechas para ver los precios en diferentes periodos de viaje.'
			: 'Select multiple pickup dates to compare. Each date becomes a main column header in the results table. You can add several dates to see pricing across different travel periods.',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByLabel('Providers') || findByLabel('Categories') || findByLabel('Tiers')
			if (el) {
				let parent = el.closest('.MuiGrid-container')
				if (parent?.parentElement) {
					const box = parent.parentElement
					const grids = box.querySelectorAll('.MuiGrid-container')
					if (grids.length >= 2) return box
				}
				return parent
			}
			return null
		},
		icon: 'filter',
		title: ES ? 'Filtros de Búsqueda' : 'Search Filters',
		description: ES
			? 'Configura tu consulta de precios: selecciona oficinas, sitios/mercados, niveles, proveedores, categorías, duración del alquiler (LOR), código ACRISS, transmisión y moneda. Todos los campos acotan los resultados para mostrar exactamente lo que necesitas.'
			: 'Configure your pricing query: select offices, sites/markets, tiers, providers, categories, LOR, ACRISS code, transmission, and currency. All fields narrow down the results to show exactly what you need.',
		example: ES
			? 'Oficinas + Proveedores + Categorías + LOR + Moneda → Buscar'
			: 'Offices + Providers + Categories + LOR + Currency → Search',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Pricing Environment')
			if (el) {
				const paper = el.closest('.MuiPaper-root')
				if (paper) return paper
			}
			const tables = document.querySelectorAll('table')
			for (const t of tables) {
				if (t.textContent.includes('Branch') || t.textContent.includes('Providers')) {
					return t.closest('.MuiPaper-root') || t
				}
			}
			return null
		},
		icon: 'table-cells',
		title: ES ? 'Tabla de Precios' : 'Pricing Table',
		description: ES
			? 'La tabla principal de resultados. La columna izquierda (fija) muestra el nombre de la oficina y los proveedores. Cada columna de fecha se divide en subcolumnas por semana de reserva. Las celdas muestran precios con bordes codificados por color e indicadores de evolución.'
			: 'The main results table. The left column (sticky) shows the office name and providers. Each date column is split into booking week sub-columns. Cells show prices with color-coded borders and evolution indicators.',
		example: ES
			? 'Borde verde = Más barato | Borde rojo = Más caro | Gris = Neutro'
			: 'Green border = Cheapest | Red border = Most Expensive | Grey = Neutral',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Min. Price') || findByText('Avg. Price') || findByText('Max. Price')
			if (el) {
				const toggle = el.closest('.MuiToggleButtonGroup-root') || el.closest('[role="group"]')
				if (toggle) return toggle
			}
			return null
		},
		skipIf: () => !findByText('Min. Price'),
		icon: 'sliders',
		title: ES ? 'Selector de Agregación de Precios' : 'Price Aggregation Toggle',
		description: ES
			? 'Alterna entre precio mínimo, medio y máximo. La tabla recalcula todas las celdas para mostrar la agregación seleccionada. Útil para comparar escenarios de precios en el mejor y peor caso.'
			: 'Switch between minimum, average, and maximum prices. The table recalculates all cells to show the selected aggregation. Useful for comparing best-case vs worst-case pricing scenarios.',
		example: ES
			? 'Precio Mín. | Precio Medio | Precio Máx.'
			: 'Min. Price | Avg. Price | Max. Price',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Branch') || findByText('Providers')
			if (el) {
				const header = el.closest('th') || el.closest('[class*="HeaderCell"]')
				if (header) return header
			}
			return null
		},
		skipIf: () => !findByText('Branch'),
		icon: 'building',
		title: ES ? 'Columna de Oficina y Proveedores' : 'Office & Providers Column',
		description: ES
			? 'La columna fija de la izquierda muestra el nombre de cada oficina con sus proveedores asociados listados debajo. Esta columna permanece fija mientras te desplazas horizontalmente por las columnas de fechas, manteniendo el contexto visible en todo momento.'
			: 'The sticky left column shows each office name with its associated providers listed below. This column stays fixed while you scroll horizontally through the date columns, keeping context visible at all times.',
	},
	{
		type: 'field',
		findTarget: () => {
			const cells = document.querySelectorAll('td')
			for (const cell of cells) {
				const box = cell.querySelector('[class*="MuiBox-root"]')
				if (box) {
					const style = window.getComputedStyle(box)
					if (style.borderWidth && style.borderWidth !== '0px' && box.textContent.includes('€')) {
						return cell.closest('tr') || cell
					}
				}
			}
			return null
		},
		skipIf: () => {
			const cells = document.querySelectorAll('td')
			for (const cell of cells) {
				if (cell.textContent.includes('€')) return false
			}
			return true
		},
		icon: 'fire-flame-curved',
		title: ES ? 'Celdas de Precio y Evolución' : 'Price Cells & Evolution',
		description: ES
			? 'Cada celda muestra un precio con un borde de color: verde para el más barato, rojo para el más caro y gris para neutro. Debajo del precio, flechas indican la evolución respecto al periodo anterior — flecha arriba significa que el precio subió, flecha abajo que bajó, junto con el porcentaje de cambio.'
			: 'Each cell shows a price with a colored border: green for cheapest, red for most expensive, and grey for neutral. Below the price, arrows indicate evolution compared to the previous period — up arrow means price increased, down arrow means decreased, with the percentage change.',
		example: ES
			? '42,30 € ↑ 5,2% (subió) | 38,10 € ↓ 3,1% (bajó)'
			: '€42.30 ↑ 5.2% (increased) | €38.10 ↓ 3.1% (decreased)',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Selecciona tus oficinas, proveedores, fechas y semanas de reserva, y pulsa buscar. Usa el selector Mín./Media/Máx. para cambiar de perspectiva. Desplázate horizontalmente para comparar fechas mientras la columna de oficinas permanece fija a la izquierda.'
			: 'Select your offices, providers, dates, and booking weeks, then hit search. Use the Min/Avg/Max toggle to switch perspectives. Scroll horizontally to compare across dates while the office column stays pinned on the left.',
	},
]
